import { useState } from "react";
import { listAccountConfigs } from "../api/config";
import { exportUsers } from "../api/users";
import type { ExportUserItemDto } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { formatBeijingTime } from "../utils/datetime";

const TIERS = ["", "pro", "proplus", "promax", "power"];

/** 目标账号下拉框"全部"选项对应的取值,后端 export_users_handler 识别为跨账号导出。 */
const ALL_ACCOUNTS = "ALL";

/**
 * 用户导出页(新增页面):按目标账号导出邮箱/用户名/层级/密码,
 * 支持按创建日期区间/邮箱/用户名/层级筛选。仅 Admin 可见入口
 * (对应后端 export_users_handler 的 ADMIN_ONLY 限制,§6.3 密码相关权限)。
 */
export function ExportUsersPage() {
  const accounts = useAsync(() => listAccountConfigs(), []);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [tier, setTier] = useState("");

  const [results, setResults] = useState<ExportUserItemDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountId = selectedAccount || accounts.data?.accounts[0]?.account_id || "";

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      setError("请先选择目标账号");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await exportUsers({
        accountId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        email: email || undefined,
        username: username || undefined,
        tier: tier || undefined,
      });
      setResults(resp.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查询失败");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!results || results.length === 0) return;
    const header =
      accountId === ALL_ACCOUNTS
        ? ["account_id", "email", "username", "tier", "password", "created_at"]
        : ["email", "username", "tier", "password", "created_at"];
    const escapeCsvCell = (value: unknown) => {
      const str = value === null || value === undefined ? "" : String(value);
      // 字段里出现逗号/引号/换行时需要用双引号包裹,内部的双引号要转义成两个双引号
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const lines = [
      header.join(","),
      ...results.map((r) =>
        header
          .map((key) =>
            key === "created_at"
              ? escapeCsvCell(formatBeijingTime(r.created_at))
              : escapeCsvCell(r[key as keyof ExportUserItemDto])
          )
          .join(",")
      ),
    ];
    // 加 UTF-8 BOM,避免 Excel 打开中文/特殊字符时乱码
    const csvContent = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.href = url;
    link.download = `kiro-users-${accountId}-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h2 className="section-title">导出用户</h2>

      <div className="card">
        <form onSubmit={handleSearch}>
          <div className="form-row">
            <label>目标账号</label>
            <select value={accountId} onChange={(e) => setSelectedAccount(e.target.value)}>
              <option value={ALL_ACCOUNTS}>全部</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>创建日期从</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <label style={{ marginLeft: 12 }}>到</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="form-row">
            <label>邮箱包含</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="子串匹配,不区分大小写" />
          </div>
          <div className="form-row">
            <label>用户名包含</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="子串匹配,不区分大小写" />
          </div>
          <div className="form-row">
            <label>层级</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t || "(全部)"}
                </option>
              ))}
            </select>
          </div>
          <button className="primary" type="submit" disabled={loading}>
            {loading ? "查询中..." : "查询"}
          </button>
        </form>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {results && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <p style={{ margin: 0 }}>共 {results.length} 条记录</p>
            <button className="primary" onClick={handleDownloadCsv} disabled={results.length === 0}>
              下载 CSV
            </button>
          </div>
          <table>
            <thead>
              <tr>
                {accountId === ALL_ACCOUNTS && <th>账号 ID</th>}
                <th>邮箱</th>
                <th>用户名</th>
                <th>层级</th>
                <th>密码</th>
                <th>创建时间</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={`${r.account_id}#${r.email}`}>
                  {accountId === ALL_ACCOUNTS && <td>{r.account_id}</td>}
                  <td>{r.email}</td>
                  <td>{r.username}</td>
                  <td>{r.tier}</td>
                  <td style={{ fontFamily: "monospace" }}>{r.password}</td>
                  <td>{formatBeijingTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {results.length === 0 && <p style={{ color: "#64748b" }}>没有匹配的记录</p>}
        </div>
      )}
    </div>
  );
}
